import { useCallback, useEffect, useRef, useState } from "react";
import { $apiV1 } from "../../../../config/api";
import { findMaxSecondMarker } from "../../../../helpers";
import {
  buildMarkerColorMap,
  buildParameterColorMap,
} from "../../../../helpers/eventMarkerColors";

const DEFAULT_TIMELINE_RANGE = { min: 0, max: 90 };

export const usePlayerSingleSideEventsData = ({
  id,
  matchesList,
  checkedIds,
  checkedParamIds = checkedIds,
  checkedAction,
  mergeOffset,
  buildSearchPayload,
  onError,
  searchDeps = [],
  initialTimelineRange = DEFAULT_TIMELINE_RANGE,
}) => {
  const initialTimelineMin =
    initialTimelineRange?.min ?? DEFAULT_TIMELINE_RANGE.min;
  const initialTimelineMax =
    initialTimelineRange?.max ?? DEFAULT_TIMELINE_RANGE.max;

  const [markers, setMarkers] = useState([]);
  const [response, setResponse] = useState(null);
  const [markerColorMap, setMarkerColorMap] = useState({});
  const [allMergeMarkers, setAllMergeMarkers] = useState([]);
  const [timeLineRange, setTimeLineRange] = useState({
    min: initialTimelineMin,
    max: initialTimelineMax,
  });
  const [loadingMarkers, setLoadingMarkers] = useState(false);

  const pendingRequestsRef = useRef(0);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const updateLoading = useCallback((delta) => {
    pendingRequestsRef.current += delta;
    setLoadingMarkers(pendingRequestsRef.current > 0);
  }, []);

  const resetData = useCallback(() => {
    setMarkers([]);
    setResponse(null);
    setMarkerColorMap({});
    setAllMergeMarkers([]);
    setTimeLineRange({
      min: initialTimelineMin,
      max: initialTimelineMax,
    });
  }, [initialTimelineMax, initialTimelineMin]);

  const fetchPlayerStats = useCallback(async () => {
    updateLoading(1);

    try {
      const statsResponse = await $apiV1.get(`/stats/player/${id}`, {
        params: {
          matches: matchesList.join(","),
          params: checkedParamIds.join(","),
        },
      });

      const allMarkers = statsResponse.data.stats.flatMap((stat) =>
        stat.parameters.flatMap((param) => param.markers)
      );
      const parameterColorMap = buildParameterColorMap(checkedAction);
      const nextMarkerColorMap = buildMarkerColorMap(
        statsResponse?.data?.stats,
        parameterColorMap
      );

      setMarkerColorMap(nextMarkerColorMap);
      if (allMarkers.length === 0) {
        setResponse(null);
        setAllMergeMarkers([]);
      }
      setMarkers(allMarkers);
    } catch (error) {
      onErrorRef.current?.(error);
      console.error("Error fetching player stats:", error);
    } finally {
      updateLoading(-1);
    }
  }, [
    id,
    matchesList,
    checkedParamIds,
    checkedAction,
    updateLoading,
  ]);

  const searchEvents = useCallback(
    async (markersToSearch) => {
      updateLoading(1);

      try {
        const basePayload =
          buildSearchPayload?.({ markers: markersToSearch }) || {};
        const payload = {
          ...basePayload,
          markers: basePayload.markers || markersToSearch,
          to_merge: false,
        };

        const result = await $apiV1.post("/stats/markers_search", payload);

        setResponse(result.data);
        setAllMergeMarkers(result.data.allMarkers);

        const max = findMaxSecondMarker(result.data.events);
        setTimeLineRange({ min: 0, max: Math.ceil(+max / 60 / 5) * 5 });
      } catch (error) {
        onErrorRef.current?.(error);
        console.error("Error searching player events:", error);
      } finally {
        updateLoading(-1);
      }
    },
    [buildSearchPayload, updateLoading]
  );

  useEffect(() => {
    if (matchesList.length > 0 && checkedParamIds.length > 0) {
      fetchPlayerStats();
      return;
    }

    resetData();
  }, [
    matchesList,
    checkedParamIds,
    checkedAction,
    fetchPlayerStats,
    resetData,
  ]);

  useEffect(() => {
    if (markers.length > 0) {
      searchEvents(markers);
    }
  }, [markers, mergeOffset, searchEvents, ...searchDeps]);

  return {
    markers,
    setMarkers,
    response,
    setResponse,
    markerColorMap,
    setMarkerColorMap,
    allMergeMarkers,
    timeLineRange,
    setTimeLineRange,
    loadingMarkers,
    resetData,
  };
};

export default usePlayerSingleSideEventsData;
